import {
  Box,
  Paper,
  Stack,
  TextField,
  InputAdornment,
  Button,
  useTheme,
  Snackbar,
  Alert,
  Skeleton,
  useMediaQuery,
  IconButton,
  darken,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import {
  Add,
  Search,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank,
  SwapVertOutlined,
  SaveAlt,
} from "@mui/icons-material";

import { useOutletContext } from "react-router-dom";
import CardMapsHome from "../../../../componentes/easyMaps/cardMapsHome/index.tsx";
import NewMap from "../../../../componentes/easyMaps/newMap";
import MenuSort from "../../../../componentes/easyMaps/sideBarEasyMapsRp/menuSort";
import ModalImportZabbix from "../../../../componentes/easyMaps/modalImportZabbix";
const useStyles = (theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
    mt: 2,
    mb: 1,
  },
  body: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
    gap: 1,
  },
  cardsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 350px), 1fr))",
    gap: 2,
    width: "100%",
  },
  headerBox: {
    display: "flex",
    flexDirection: "column",
    gap: 1,
    width: "100%",
  },

  addIcon: { mt: -0.2 },
  headerPaper: {
    boxShadow: "0px 0px 5px  #0000001a",
    mb: "15px",
    borderTopLeftRadius: "0px",
    padding: "16px",
    display: "flex",
    gap: "16px",
    flexDirection: "column",
    position: "relative",
    width: "100%",
  },
  buttonDialog: {
    color: theme?.palette?.color?.buttonConfirm || "primary.main",
  },
  button: {
    width: { xs: "100%", sm: "fit-content" },
    minWidth: "148px",
    textTransform: "none",
    boxShadow: "0px 0px 3px #0000001a",
    "&:hover": {
      backgroundColor: darken(theme.palette.primary.main, 0.2),
      boxShadow: "0px 0px 4px #0000001a",
    },
  },
  textfield: { width: "360px" },
  tabs: {
    zIndex: 1,
    ml: "-2px",
    "& button": {
      ml: "2px",
      mt: "1px",
      backgroundColor: theme.palette.background.tab,
      boxShadow: "0px 0px 5px  #0000001a",
      color: theme.palette.text.disable,
      textTransform: "none",
      borderTopLeftRadius: "5px",
      borderTopRightRadius: "5px",
      minWidth: "120px",
      minHeight: "50px",
    },
    "& button.Mui-selected": {
      backgroundColor: theme.palette.background.paper,
    },
    "& .MuiTabs-indicator": {
      backgroundColor: "transparent",
      border: "none",
    },
  },
});

export default function MapaTopologiaEasyMaps() {
  const {
    maps,
    loadingPage,
    setMaps,
    handleApiGetMap,
    handleApiGetMaps,
    removeMapFromState,
  } = useOutletContext();
  const [tabValue, setTabValue] = useState(0);
  const [anchorElOrder, setAnchorElOrder] = useState(null);
  const [directionSort, setDirectionSort] = useState(false);
  const [sort, setSort] = useState(0);
  const [openNovoMapaDialog, setOpenNovoMapaDialog] = useState(false);
  const [openImportZabbix, setOpenImportZabbix] = useState(false);
  const [snackbar, setSnackbar] = useState(null);
  const [textFilter, setTextFilter] = useState("");
  const handleCloseSnackbar = () => setSnackbar(null);
  const theme = useTheme();
  const styles = useStyles(theme);
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    setTextFilter("");
  }, [tabValue]);

  const mapasFilter = useMemo(() => {
    // Filtra
    let result = maps.filter(
      (map) =>
        map.titulo.toLowerCase().includes(textFilter.toLowerCase()) &&
        map.tipo === 1,
    );

    // Ordena baseado no critério
    const comparators = {
      0: (a, b) => a.titulo.localeCompare(b.titulo),
      1: (a, b) => {
        // Soma todos os valores (value) dos alertas
        const totalA =
          a.alertas?.reduce((acc, alerta) => acc + (alerta.value || 0), 0) || 0;
        const totalB =
          b.alertas?.reduce((acc, alerta) => acc + (alerta.value || 0), 0) || 0;
        return totalA - totalB;
      },
      2: (a, b) =>
        (a.mapas?.length + a.pops?.length + a.hosts?.length || 0) -
        (b.mapas?.length + b.pops?.length + b.hosts?.length || 0),
    };

    if (sort !== undefined && comparators[sort]) {
      result = result.sort((a, b) => {
        const comparison = comparators[sort](a, b);
        return directionSort === false ? comparison : -comparison;
      });
    }
    return result;
  }, [maps, textFilter, sort, directionSort]);

  const deleteMapsInMaps = removeMapFromState;

  function addMapInMaps(map) {
    const newMaps = [...maps];
    newMaps.push(map);
    setMaps(newMaps);
  }

  function updateMapTokenInMaps(mapToken, indexMap) {
    let newMaps = [...maps];
    newMaps[indexMap]["mapToken"] = mapToken;
    setMaps(newMaps);
  }

  function updateTitleInMaps(newTitle, mapId) {
    const newMaps = maps.map((map) =>
      map.id === mapId ? { ...map, titulo: newTitle } : map,
    );
    setMaps(newMaps);
  }

  return (
    <Box sx={styles.container}>
      <Box sx={styles.body}>
        <Stack width="100%">
          <Paper sx={styles.headerPaper}>
            <Box sx={styles.headerBox}>
              <Stack
                direction={"row"}
                justifyContent={"space-between"}
                alignItems={"center"}
                flexWrap={"wrap"}
                gap={1}
              >
                <Stack direction={"row"} alignItems={"center"} gap={0.5}>
                  <TextField
                    placeholder={"Buscar"}
                    autoComplete="off"
                    size="small"
                    fullWidth={isSmDown}
                    value={textFilter}
                    onChange={(event) => {
                      setTextFilter(event.target.value);
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor:
                          theme.palette.background.paperLinkedMaps,
                        boxShadow: "none",
                        width: { xs: "100%", sm: "300px" },
                        "&:hover": {
                          backgroundColor:
                            theme.palette.background.paperLinkedMaps,
                        },
                      },
                    }}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <Search />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                  <IconButton
                    size="small"
                    sx={{ width: "40px", height: "40px" }}
                    onClick={(event) => setAnchorElOrder(event.currentTarget)}
                  >
                    <SwapVertOutlined />
                  </IconButton>
                </Stack>
                <Stack
                  direction={"row"}
                  justifyContent={"center"}
                  alignItems={"center"}
                  gap={1}
                  flexWrap={"wrap"}
                >
                  <Button
                    color="primary"
                    variant="contained"
                    sx={{
                      ...styles.button,

                      "&:hover .download-icon": {
                        animation: "downloadBounce 1s ease infinite",
                      },

                      "@keyframes downloadBounce": {
                        "0%, 100%": {
                          transform: "translateY(-2px)",
                        },
                        "50%": {
                          transform: "translateY(2px)",
                        },
                      },
                    }}
                    endIcon={<SaveAlt className="download-icon" />}
                    onClick={() => {
                      setOpenImportZabbix(true);
                    }}
                  >
                    Importar do Zabbix
                  </Button>

                  <Button
                    color="primary"
                    variant="contained"
                    sx={{
                      ...styles.button,

                      "&:hover .rotate-icon": {
                        transform: "rotate(180deg)",
                      },
                    }}
                    endIcon={
                      <Add
                        className="rotate-icon"
                        sx={{
                          transition: "transform 0.4s ease",
                        }}
                      />
                    }
                    onClick={() => {
                      setOpenNovoMapaDialog(true);
                    }}
                  >
                    Novo Mapa
                  </Button>
                </Stack>
              </Stack>
            </Box>
          </Paper>
        </Stack>
        {loadingPage ? (
          <Box sx={styles.cardsGrid}>
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton
                key={index}
                variant="rectangular"
                sx={{ width: "100%", height: "185px", borderRadius: "6px" }}
              />
            ))}
          </Box>
        ) : (
          <Box sx={styles.cardsGrid}>
            {Array.isArray(mapasFilter) &&
              mapasFilter.length > 0 &&
              mapasFilter.map((map, index) => (
                <CardMapsHome
                  key={index}
                  index={index}
                  id={map.id}
                  mapName={map.titulo}
                  tipo={map.tipo}
                  inGroup={false}
                  alertas={map.alertas}
                  hostsNames={[map.hosts]}
                  hostsCounts={map.hosts?.length || 0}
                  popsCounts={map.pops?.length || 0}
                  mapasCounts={map.mapas?.length || 0}
                  deleteMapsInMaps={deleteMapsInMaps}
                  duplicateMapsInMaps={handleApiGetMap}
                  updateTitleInMaps={updateTitleInMaps}
                  updateMapTokenInMaps={updateMapTokenInMaps}
                  setSnackbar={setSnackbar}
                  mapas={map?.mapas}
                  pops={map?.pops}
                  mapToken={map?.mapToken}
                />
              ))}
          </Box>
        )}
      </Box>
      <NewMap
        key={"newMapa-Main"}
        openNovoMapaDialog={openNovoMapaDialog}
        setOpenNovoMapaDialog={setOpenNovoMapaDialog}
        setSnackbar={setSnackbar}
        maps={maps}
        initialtab={1}
        addMapInMaps={addMapInMaps}
        tipoDefault={1}
      />
      <ModalImportZabbix
        open={openImportZabbix}
        setOpen={setOpenImportZabbix}
        onImportSuccess={async () => {
          handleApiGetMaps();
          setSnackbar({
            children: "Mapas importados com sucesso!",
            severity: "success",
          });
        }}
      />
      <MenuSort
        anchorEl={anchorElOrder}
        setAnchorEl={setAnchorElOrder}
        options={["Nome", "Alertas", "Elementos"]}
        direction={directionSort}
        setDirection={setDirectionSort}
        sort={sort}
        setSort={setSort}
      />

      {!!snackbar && (
        <Snackbar
          open
          onClose={() => setSnackbar(null)}
          autoHideDuration={2000}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert {...snackbar} onClose={handleCloseSnackbar} />
        </Snackbar>
      )}
    </Box>
  );
}
